/* eslint-disable react/no-unused-prop-types */
import BuildShowStore from "~/stores/BuildShowStore";
import { Build } from "~/components/build/Show/lib/types";
import {
  BuildContextProvider,
  LegacyBuildContextProvider,
  useBuild,
} from "~/components/build/Show/lib/BuildContext";
import {
  RouterProvider,
  Navigate,
  RouteObject,
  useNavigate,
  NonIndexRouteObject,
  useRouteError,
  useParams,
  isRouteErrorResponse,
} from "react-router-dom";

// Use react-router with support for Datadog RUM (when enabled)
// @see https://docs.datadoghq.com/integrations/rum-react/#react-router-integration
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";

import Database from "~/lib/Database";
import { ErrorView } from "~/components/shared/ErrorView";
import BuildShowLayout from "~/components/build/Show/layouts/BuildShowLayout";
import JobsPage from "~/components/build/Show/pages";
import SummaryPage from "~/components/build/Show/pages/summary";
import AnnotationsPage from "~/components/build/Show/pages/annotations";
import TestsPage from "~/components/build/Show/pages/tests";
import { parseHash } from "~/stores/BuildPageStore";
import Overview from "~/components/build/Show/pages/summary/overview";
import { StrictMode, useEffect } from "react";
import Bugsnag from "~/lib/Bugsnag";
import { type FaviconPaths } from "~/lib/Favicon";
import { useFaviconPathsStore } from "~/components/build/Show/lib/useFaviconPathsStore";
import { useWaterfallAvailableStore } from "~/components/build/Show/lib/useWaterfallAvailableStore";
import StepUploads from "~/components/build/Show/pages/summary/step-uploads";
import { urlForView } from "~/components/build/Show/lib/urlForView";
import {
  StepsDisplayMode,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useJobs } from "~/components/build/Show/lib/useJobs";
import { getJobForStep } from "~/components/build/Show/lib/getJobForStep";
import { Type } from "~/lib/pipeline/Step";

import StepsLayout, { StepsIndexRedirect } from "~/components/build/Show/layouts/StepsLayout";
import WaterfallPage from "~/components/build/Show/pages/steps/waterfall";
import TablePage from "~/components/build/Show/pages/steps/table";
import CanvasPage from "~/components/build/Show/pages/steps/canvas";
import ListPage from "~/components/build/Show/pages/steps/list";

import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import initDatadogRum, {
  DatadogRumInitConfiguration,
} from "~/components/build/Show/lib/initDatadogRum";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useWindowFocusRefetch } from "~/components/build/Show/lib/useWindowFocusRefetch";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { FilterByOption, useFilterStore } from "~/components/build/Show/lib/useFilterStore";
import { HotkeysProvider } from "react-hotkeys-hook";

const waterfallRoute: NonIndexRouteObject = {
  path: "waterfall",
  id: BuildView.Waterfall,
  element: <WaterfallPage allowCollapse={false} />,
  errorElement: <RouteError />,
};

const useDefaultViewRedirect = () => {
  const { build } = useBuild();
  const { pipeline } = useParams<{ pipeline: string }>();
  const currentParams = useQueryParams();

  const getDefaultView = useBuildPreferencesStore((state) => state.getDefaultView);
  const defaultView = getDefaultView(pipeline || "");

  // Re-write "classic" hash style job UUID to query parameter.
  const hash = parseHash(window.location.hash);
  const params = { ...currentParams };
  if (hash && hash.job) {
    params.jobUuid = hash.job;
  }

  // If DefaultToAnnotationsTab feature is true, preference that over user default view
  if (Features.DefaultToAnnotationsTab) {
    return urlForView(build.path, BuildView.Annotations, params);
  }

  // If the user has a default view set, redirect to that.
  if (defaultView === BuildView.Tests && !build.testEngine.testTabUrls?.tabUrl) {
    // Fallback to the summary view if the tests tab is not available
    return urlForView(build.path, BuildView.Summary, params);
  }

  return urlForView(build.path, defaultView, params);
};

/**
 * Handle redirecting if the user navigates to the base route
 * i.e. `/:org/:pipeline/builds/:number/`
 */
function BaseRouteRedirect() {
  let url = useDefaultViewRedirect();

  // Preserve the hash fragment (for both classic build and better build UI URLs)
  const hash = parseHash(window.location.hash);
  if (Features.BuildUIUrlRedirects && hash?.job) {
    // Classic UI format: preserve the full hash (e.g., #job-uuid/22-25 or #job-uuid/L54)
    url += window.location.hash;
  } else if (hash?.line && hash?.group) {
    // Group-line format: /{group}-{line}
    url += `#/${hash.group}-${hash.line}`;
  } else if (hash?.line && !hash?.group) {
    // Line-only format: /L{line}
    url += `#/L${hash.line}`;
  } else if (hash?.group) {
    // Group only format: /{group}
    url += `#/${hash.group}`;
  }

  return <Navigate to={url} replace={true} />;
}

function StepUploadsRedirect() {
  const { org, pipeline, number } = useParams();
  const redirectPath = `/${org}/${pipeline}/builds/${number}/step-uploads`;

  // Step uploads live on a server-side route for the classic page so we need to do a full
  // redirect
  useEffect(() => {
    window.location.href = redirectPath;
  }, [redirectPath]);

  return null;
}

/**
 * Builds a hash fragment for Classic Build shaped URLs.
 *
 * @param hashString - Raw hash string to extract line range (and optionally job UUID) from
 * @param jobUuid - Job UUID (optional - if not provided, extracted from hashString)
 * @returns Hash fragment (e.g., "#uuid/22-25", "#uuid/L54", "#uuid/3", or "#uuid") or empty string if no job UUID found
 */
function buildClassicHashFragment(hashString: string, jobUuid?: string): string {
  const hash = parseHash(hashString);
  const job = jobUuid || hash?.job;

  if (!job) {
    return "";
  }

  // Build line range string: supports group-line, line-only, and group-only formats
  let lineRange = "";
  if (hash?.group && hash?.line) {
    // Group-line format: /{group}-{line}
    lineRange = `/${hash.group}-${hash.line}`;
  } else if (hash?.line && !hash?.group) {
    // Line-only format: /L{line} (range end line is discarded - classic UI doesn't support ranges)
    lineRange = `/L${hash.line}`;
  } else if (hash?.group) {
    // Group only format: /{group}
    lineRange = `/${hash.group}`;
  }

  return `#${job}${lineRange}`;
}

/**
 * Handles redirects from Better Build URLs to Classic Build views.
 *
 * Redirect scenarios:
 * 1. Job URL with jid parameter: `/steps/canvas?jid={jobId}` → `/{basePath}#{jobId}`
 * 2. Step URL with sid + job hash: `/steps/canvas?sid={stepId}#{jobId}` → `/{basePath}#{jobId}`
 * 3. Step URL with sid only: `/steps/canvas?sid={stepId}` → `/{basePath}#{selectedJobId}`
 * 4. Step/view URL without job: `/steps/canvas` → targetPath (e.g., `/canvas`, `/waterfall`, or base path)
 *
 * The Classic UI is job-based and references jobs via hash fragments, while the new UI
 * uses `sid`/`jid` query parameters. This component bridges the gap by converting query
 * parameters to hash fragments when a job reference is present or can be determined.
 *
 * @param targetPath - The fallback path to redirect to when no job reference is present
 */
function BetterBuildToClassicRedirect({ targetPath }: { targetPath: string }) {
  const { org, pipeline, number } = useParams();
  const queryParams = useQueryParams();
  const basePath = `/${org}/${pipeline}/builds/${number}`;
  const { stepsMap, stepJobs } = useSteps();
  const jobs = useJobs();

  // Better builds use `jid` query parameters to reference jobs but the classic build UI
  // references jobs via the hash so we'll redirect to the hashed version when we see a
  // `jid` param.
  if (queryParams.jobUuid) {
    const hashFragment = buildClassicHashFragment(window.location.hash, queryParams.jobUuid);
    const redirectPath = `${basePath}${hashFragment}`;
    return <Navigate to={redirectPath} replace={true} />;
  }

  // If we have a `sid` (step ID) param, handle step-based redirects:
  // 1. With job hash: Redirect with the hash (e.g., ?sid={stepId}#{jobId}/1-10)
  // 2. Without hash: Select appropriate job based on step type and redirect
  if (queryParams.stepUuid) {
    const hashFragment = buildClassicHashFragment(window.location.hash);
    if (hashFragment) {
      const redirectPath = `${basePath}${hashFragment}`;
      return <Navigate to={redirectPath} replace={true} />;
    }

    if (stepsMap.size === 0) {
      return null;
    }

    const step = stepsMap.get(queryParams.stepUuid);
    const stepJobsList = stepJobs.get(queryParams.stepUuid);

    if (step && stepJobsList) {
      // Check if this step type should redirect to a job
      // Wait steps: Classic Build can't scroll to them (manual testing confirmed)
      // Block/Input steps: Would scroll to job but won't open unblock dialog (suboptimal UX)
      const shouldRedirectToJob =
        step.type !== Type.Wait && step.type !== Type.Block && step.type !== Type.Input;

      if (shouldRedirectToJob) {
        const jobUuid = getJobForStep(step, stepJobsList, jobs);

        if (jobUuid) {
          const hashFragment = `#${jobUuid}`;
          const redirectPath = `${basePath}${hashFragment}`;
          return <Navigate to={redirectPath} replace={true} />;
        }
      }
    }
  }

  return <Navigate to={targetPath} replace={true} />;
}

function CatchAllRoute() {
  const queryParams = useQueryParams();
  const { org, pipeline, number, ...rest } = useParams();
  const setDisplayMode = useBuildPreferencesStore((state) => state.setStepsDisplayMode);

  const basePath = `/${org}/${pipeline}/builds/${number}`;
  let redirectPath;

  switch (rest["*"]) {
    case "waterfall":
      if (Features.DisableStepsWaterfallView) {
        // Fall back to table view for legacy URLs
        // We won't persist this to their preferences as they may belong to other
        // organizations where this would be surprising
        setDisplayMode(StepsDisplayMode.Table);
        redirectPath = urlForView(basePath, BuildView.Table, queryParams);
      } else {
        setDisplayMode(StepsDisplayMode.Waterfall);
        redirectPath = urlForView(basePath, BuildView.Waterfall, queryParams);
      }
      break;
    case "canvas":
      if (Features.DisableStepsCanvasView) {
        // Fall back to table view for legacy URLs
        // We won't persist this to their preferences as they may belong to other
        // organizations where this would be surprising
        setDisplayMode(StepsDisplayMode.Table);
        redirectPath = urlForView(basePath, BuildView.Table, queryParams);
      } else {
        setDisplayMode(StepsDisplayMode.Canvas);
        redirectPath = urlForView(basePath, BuildView.Canvas, queryParams);
      }
      break;
    case "table":
      setDisplayMode(StepsDisplayMode.Table);
      redirectPath = urlForView(basePath, BuildView.Table, queryParams);
      break;
    default:
      redirectPath = basePath;
      break;
  }

  redirectPath = `${redirectPath}${window.location.hash}`;

  return <Navigate to={redirectPath} replace={true} />;
}

export const routes: RouteObject[] = Features.BuildSidebar
  ? [
      {
        path: ":org/:pipeline/builds/:number/",
        element: <BuildShowLayout />,
        errorElement: <RouteError />,
        children: [
          {
            id: BuildView.Index,
            index: true,
            element: <BaseRouteRedirect />,
            errorElement: <RouteError />,
          },
          {
            path: "summary",
            id: BuildView.Summary,
            element: <SummaryPage />,
            errorElement: <RouteError />,
            children: [
              {
                index: true,
                element: <Overview />,
                errorElement: <RouteError />,
              },
              {
                path: "step-uploads",
                element: <StepUploads />,
                errorElement: <RouteError />,
              },
            ],
          },
          {
            path: "steps",
            id: BuildView.Steps,
            element: <StepsLayout />,
            errorElement: <RouteError />,
            children: [
              {
                index: true,
                element: <StepsIndexRedirect />,
                errorElement: <RouteError />,
              },
              {
                path: "table",
                id: BuildView.Table,
                element: <TablePage />,
                errorElement: <RouteError />,
              },
              {
                path: "canvas",
                id: BuildView.Canvas,
                element: <CanvasPage />,
                errorElement: <RouteError />,
              },
              ...(Features.BuildPageListView
                ? [
                    {
                      path: "list",
                      id: BuildView.List,
                      element: <ListPage />,
                      errorElement: <RouteError />,
                    },
                  ]
                : []),
              {
                path: "waterfall",
                id: BuildView.Waterfall,
                element: <WaterfallPage allowCollapse={true} />,
                errorElement: <RouteError />,
              },
            ],
          },
          {
            path: "annotations",
            id: BuildView.Annotations,
            element: <AnnotationsPage />,
            errorElement: <RouteError />,
          },
          {
            path: "tests",
            id: BuildView.Tests,
            element: <TestsPage />,
            errorElement: <RouteError />,
          },
          {
            // Catch all unknown paths and redirect to the build root
            path: "*",
            element: <CatchAllRoute />,
            errorElement: <RouteError />,
          },
        ],
      },
    ]
  : [
      {
        path: ":org/:pipeline/builds/:number/",
        element: <BuildShowLayout />,
        errorElement: <RouteError />,
        children: [
          {
            index: true,
            id: BuildView.JobList,
            element: <JobsPage />,
            errorElement: <RouteError />,
          },
          {
            path: "canvas",
            id: BuildView.Canvas,
            element: <CanvasPage />,
            errorElement: <RouteError />,
          },
          waterfallRoute,
          {
            path: "tests",
            id: BuildView.Tests,
            element: <TestsPage />,
            errorElement: <RouteError />,
          },
          // Redirects to handle user switching back to classic.
          ...(Features.BuildUIUrlRedirects
            ? [
                {
                  path: "steps/table",
                  element: <BetterBuildToClassicRedirect targetPath=".." />, // Redirect to the job list (or base path if jid param)
                  errorElement: <RouteError />,
                },
                {
                  path: "steps/waterfall",
                  element: <BetterBuildToClassicRedirect targetPath="../waterfall" />, // Redirect to the waterfall view (or base path if jid param)
                  errorElement: <RouteError />,
                },
                {
                  path: "steps/canvas",
                  element: <BetterBuildToClassicRedirect targetPath="../canvas" />, // Redirect to the canvas view (or base path if jid param)
                  errorElement: <RouteError />,
                },
                {
                  path: "steps/list",
                  element: <BetterBuildToClassicRedirect targetPath=".." />, // Redirect to the job list (list view doesn't exist in classic)
                  errorElement: <RouteError />,
                },
                {
                  path: "summary/step-uploads",
                  element: <StepUploadsRedirect />, // Redirect to the step-uploads view
                  errorElement: <RouteError />,
                },
              ]
            : [
                {
                  path: "steps/table",
                  element: <Navigate to=".." replace={true} />, // Redirect to the job list
                  errorElement: <RouteError />,
                },
                {
                  path: "steps/waterfall",
                  element: <Navigate to="../waterfall" replace={true} />, // Redirect to the waterfall view
                  errorElement: <RouteError />,
                },
                {
                  path: "steps/canvas",
                  element: <Navigate to="../canvas" replace={true} />, // Redirect to the canvas view
                  errorElement: <RouteError />,
                },
                {
                  path: "steps/list",
                  element: <Navigate to=".." replace={true} />, // Redirect to the job list (list view doesn't exist in classic)
                  errorElement: <RouteError />,
                },
              ]),
          {
            path: "*", // Catch all unknown paths and redirect to the job list
            element: <CatchAllRoute />,
            errorElement: <RouteError />,
          },
        ],
      },
    ];

function RouteError() {
  const navigate = useNavigate();
  const routeError = useRouteError();

  // A 404 (Not Found) can occur when navigating to a non-existent route that's been pushed into history
  // by another client-side history action. (ie. turbo links)
  const is404 = isRouteErrorResponse(routeError) && routeError.status === 404;

  useEffect(() => {
    // If the error is a 404, don't report it to Bugsnag.
    if (is404) {
      return;
    }

    if (routeError instanceof Error) {
      Bugsnag.notify(routeError, (event) => {
        event.severity = "error";
      });
    }
  }, [routeError, is404]);

  // If the error is a 404, don't render an error message.
  if (is404) {
    return null;
  }

  // eslint-disable-next-line react/jsx-no-bind
  return <ErrorView className="px-4 py-2" clearError={() => navigate(0)} />;
}

interface PageProps {
  build: Build;
  faviconPaths: FaviconPaths;
  waterfallAvailable: boolean;
  datadogRum?: DatadogRumInitConfiguration;
}

/**
 * Adds backwards compatibility for references to job log lines that don't belong to a log
 * group. We now give those lines an id of `0` for the log group portion, but it used to
 * be `undefined`.
 */
function handleUndefinedLogGroups() {
  // modified hash match from `parseHash`
  const match = window.location.hash.match(/([^/#]+)(?:\/?(undefined|\d+)?-?(\d+)?)?/);
  if (match) {
    const [job, group, line] = match.slice(1);
    if (group === `undefined`) {
      const baseUrl = window.location.href.split("#")[0];
      const newHash = `#${job}/0-${line}`;
      window.location.href = `${baseUrl}${newHash}`;
    }
  }
}

// Browsers throttle/suspend WebSocket connections in background tabs, so
// ActionCable events can be silently lost. Stale queries are refetched when
// the user returns to the tab to recover from missed updates.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
    },
  },
});

export default (props: PageProps) => {
  useFaviconPathsStore.getState().setFaviconPaths(props.faviconPaths);
  useWaterfallAvailableStore.getState().setWaterfallAvailable(props.waterfallAvailable);

  const router = createBrowserRouter(routes);

  useWindowFocusRefetch();
  handleUndefinedLogGroups();

  // Instrument the new build experience when DatadogRUM is enabled.
  if (Features.DatadogRUM && Features.BuildSidebar && props.datadogRum) {
    initDatadogRum(props.datadogRum);
  }

  if (!Features.BuildSidebar) {
    const store = new BuildShowStore(props);
    useBuildStore.getState().setSteps(store.build.steps, store.build);
    useBuildStore.getState().setJobs(store.build.jobs);

    if (store.build.applyFailedFilter) {
      useFilterStore.getState().setFilterBy([FilterByOption.Failed]);
    }

    return (
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <LegacyBuildContextProvider store={store}>
            <HotkeysProvider>
              <RouterProvider router={router} />
            </HotkeysProvider>
          </LegacyBuildContextProvider>
        </QueryClientProvider>
      </StrictMode>
    );
  }

  const initialBuild: Build = Database.parse(props.build);

  if (initialBuild.applyFailedFilter) {
    useFilterStore.getState().setFilterBy([FilterByOption.Failed]);
  }

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BuildContextProvider initialBuild={initialBuild}>
          <HotkeysProvider>
            <RouterProvider router={router} />
          </HotkeysProvider>
        </BuildContextProvider>

        {SHOW_REACT_QUERY_DEVTOOLS === "true" && <ReactQueryDevtools />}
      </QueryClientProvider>
    </StrictMode>
  );
};
